import { useEffect, useMemo, useRef, useState } from "react";
import { createProviderRegistry } from "./providers";
import {
  EnrichmentCacheContext,
  createEnrichmentCache,
  DEFAULT_TTL_MS,
} from "./enrichment-cache";

/**
 * React provider. Exposes the cache via context so useEventTitles can
 * dispatch from anywhere in the tree.
 *
 * Cross-account isolation: when `currentUserId` changes (sign-out then
 * sign-in as a different user on the same tab), every cached title belongs
 * to the previous user's session. Defense-in-depth — even though each
 * user's UI normally only queries event keys their own Firestore data
 * references, we drop the whole cache to eliminate any chance of a stale
 * key collision surfacing the prior user's title.
 *
 * @param {{
 *   children: import('react').ReactNode,
 *   registry?: Record<string, import('./providers/types').EnrichmentProvider>,
 *   tokenProviders?: Record<string, () => Promise<object|null>>,
 *   ttlMs?: number,
 *   currentUserId?: string | null,
 * }} props
 */
export function EnrichmentCacheProvider({
  children,
  registry,
  tokenProviders = {},
  ttlMs = DEFAULT_TTL_MS,
  currentUserId = null,
}) {
  // Stable token-providers ref so the cache always reads the latest map
  // without forcing a fresh cache instance on every render.
  const tokenProvidersRef = useRef(tokenProviders);
  useEffect(() => {
    tokenProvidersRef.current = tokenProviders;
  }, [tokenProviders]);

  // Lazy-init the cache once for the lifetime of the provider. Re-creating
  // it on re-render would drop in-memory entries and re-trigger fetches.
  // The resolver below only fires *during fetches* (inside enrichment-cache's
  // batch flush), never during this component's render — the lint rule's
  // ref-during-render heuristic is a false positive here.
  // eslint-disable-next-line react-hooks/refs
  const [cache] = useState(() =>
    createEnrichmentCache({
      registry: registry || createProviderRegistry(),
      tokenProviders: (providerId) => tokenProvidersRef.current?.[providerId],
      ttlMs,
    }),
  );

  // Track the last-seen UID so we can detect transitions. Initialize lazily
  // to the first observed value so a fresh provider mount with a logged-in
  // user does NOT spuriously clear the (already-empty) cache.
  const lastUidRef = useRef(currentUserId);
  useEffect(() => {
    if (lastUidRef.current === currentUserId) return;
    lastUidRef.current = currentUserId;
    cache.clear();
  }, [currentUserId, cache]);

  const value = useMemo(
    () => ({
      request: cache.request,
      get: cache.get,
      subscribe: cache.subscribe,
      invalidate: cache.invalidate,
      clear: cache.clear,
    }),
    [cache],
  );

  return (
    <EnrichmentCacheContext.Provider value={value}>
      {children}
    </EnrichmentCacheContext.Provider>
  );
}
